import type { CatalogSort } from './CatalogSort';
import type { ScoreKind } from './ScoreKind';

/** Recognition status of a PDF/image score (photo → MusicXML pipeline). */
export type RecognitionState =
  | { state: 'none' }
  | { state: 'pending' }
  | { state: 'done' }
  | { state: 'failed'; message: string };

/**
 * One catalog entry. Mirrors Singline/Sources/Models/ScoreItem.swift — the same
 * field names are written to catalog.json, so an index is readable on either
 * platform.
 */
export interface ScoreItem {
  id: string;
  title: string;
  /** File name inside the blob store (Documents/Catalog on native). */
  fileName: string;
  kind: ScoreKind;
  /** ISO-8601 timestamp. */
  addedAt: string;
  recognition: RecognitionState;
}

export function newScoreItem(input: {
  title: string;
  fileName: string;
  kind: ScoreKind;
  id?: string;
  addedAt?: string;
  recognition?: RecognitionState;
}): ScoreItem {
  return {
    id: input.id ?? crypto.randomUUID(),
    title: input.title,
    fileName: input.fileName,
    kind: input.kind,
    addedAt: input.addedAt ?? new Date().toISOString(),
    recognition: input.recognition ?? { state: 'none' },
  };
}

export function encodeIndex(items: ScoreItem[]): string {
  const payload = items.map((item) => ({
    id: item.id,
    title: item.title,
    fileName: item.fileName,
    kind: item.kind,
    addedAt: item.addedAt,
    recognition: item.recognition,
  }));
  return `${JSON.stringify(payload, null, 2)}\n`;
}

export function decodeIndex(text: string): ScoreItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const json = raw as Record<string, unknown>;
    const kind = json.kind;
    if (kind !== 'musicxml' && kind !== 'pdf' && kind !== 'image') return [];
    if (typeof json.id !== 'string' || typeof json.title !== 'string' || typeof json.fileName !== 'string') {
      return [];
    }
    return [{
      id: json.id,
      title: json.title,
      fileName: json.fileName,
      kind,
      addedAt: typeof json.addedAt === 'string' ? json.addedAt : new Date(0).toISOString(),
      // `recognition` is absent from indexes written before the field
      // existed; those items read back as `none`, as they do on iOS.
      recognition: decodeRecognition(json.recognition),
    }];
  });
}

function decodeRecognition(raw: unknown): RecognitionState {
  if (!raw || typeof raw !== 'object') return { state: 'none' };
  const json = raw as Record<string, unknown>;
  switch (json.state) {
    case 'pending':
      return { state: 'pending' };
    case 'done':
      return { state: 'done' };
    case 'failed':
      return { state: 'failed', message: typeof json.message === 'string' ? json.message : 'Unknown error.' };
    default:
      return { state: 'none' };
  }
}

export function titleFromFileName(fileName: string): string {
  const base = fileName.includes('.') ? fileName.slice(0, fileName.lastIndexOf('.')) : fileName;
  return base
    .replace(/[_-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function uniqueFileName(fileName: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(fileName)) return fileName;
  const dot = fileName.lastIndexOf('.');
  const base = dot === -1 ? fileName : fileName.slice(0, dot);
  const ext = dot === -1 ? '' : fileName.slice(dot + 1);
  let counter = 2;
  while (true) {
    const candidate = ext === '' ? `${base} ${counter}` : `${base} ${counter}.${ext}`;
    if (!used.has(candidate)) return candidate;
    counter += 1;
  }
}

export function itemsMatching(items: ScoreItem[], query: string, sort: CatalogSort): ScoreItem[] {
  const trimmed = query.trim();
  const filtered = trimmed === ''
    ? items.slice()
    : items.filter((item) => item.title.toLocaleLowerCase().includes(trimmed.toLocaleLowerCase()));
  filtered.sort((a, b) => {
    const delta = Date.parse(a.addedAt) - Date.parse(b.addedAt);
    return sort === 'newest' ? -delta : delta;
  });
  return filtered;
}
